/**
 * Stage keys, and why some of them carry a sales role (§4.1, §4.2).
 *
 * The drill and knowledge stages are role-agnostic: reading a prospect and
 * knowing the compliance standard mean the same thing whether you are setting
 * the appointment or taking the transfer. The simulation stages are not. A
 * setter's practice call and a closer's are different content testing different
 * work, and §4.1 derives hybrid from *both* entitlements — so a rep who has
 * certified as a setter must still be able to walk the closer ladder from the
 * bottom.
 *
 * Keying those stages by role is what makes that possible. `practice:setter`
 * and `practice:closer` are separate rungs with separate progress, so passing
 * one does not silently clear the other.
 *
 * Which stages are role-scoped is a property of the org's content, not of the
 * engine, so it comes from the pack (§22.2). An org that only ever runs setters
 * configures nothing and every stage stays unscoped.
 */

export function stageKey(
  stage: string,
  salesRole: string | null | undefined,
  roleScopedStages: string[] | undefined
): string {
  return salesRole && roleScopedStages?.includes(stage) ? `${stage}:${salesRole}` : stage;
}

/** The bare stage a possibly role-scoped key belongs to. */
export const baseStage = (key: string): string => key.split(':')[0]!;

/** The role a stage key is scoped to, or null where it is not scoped. */
export const stageRole = (key: string): string | null => key.split(':')[1] ?? null;

/**
 * The track a person's ladder is built from (§4.1).
 *
 * A rep has a `salesRole`, and it is the whole of the answer: it decides which
 * scenarios are theirs and which entitlement a pass earns them.
 *
 * A manager or an admin has none — deliberately, because `salesRole === null`
 * is what marks somebody as not a rep. It keeps them off the rep roster and it
 * is how the scoring trigger knows their attempt is a rehearsal rather than a
 * certification. So the track they choose to train on is a second, separate
 * field: overloading `salesRole` for it would silently make them a rep.
 *
 * `trainingTrack` is theirs to set and change; it decides which content they
 * see and nothing else. A rep's is ignored — their salesRole is not a
 * preference.
 */
export function trainingRole(
  user: { salesRole?: string | null; trainingTrack?: string | null } | null | undefined
): string | null {
  return user?.salesRole ?? user?.trainingTrack ?? null;
}

/**
 * Whether this person's attempts earn certifications.
 *
 * Read from the *application role*, never from the user document: a manager may
 * write parts of a user document, so a document-derived answer could be edited
 * into a credential. The app role lives in a custom claim, which only the Admin
 * SDK can set.
 */
export function certifies(appRole: string | null | undefined): boolean {
  return appRole === 'rep';
}

/**
 * The claims an account carries (§7).
 *
 * `orgId` is where somebody belongs — their home company. A rep or a manager
 * has only this, and every rule they meet is the one it has always been.
 *
 * `adminOrgIds` is the set an admin may reach, and it always contains their own
 * company. It exists because one client runs seven businesses with a single set
 * of administrators, and because a rep moving between two of them has to be
 * moved by somebody who can see both. Absent for everybody else.
 */
export function claimsFor(
  orgId: string,
  role: string,
  adminOrgIds?: string[] | null
): { orgId: string; role: string; adminOrgIds?: string[] } {
  if (role !== 'admin') return { orgId, role };
  // Their own company is always in reach, and the list is deduplicated and
  // ordered so that comparing two of them is comparing two strings.
  const reach = [...new Set([orgId, ...(adminOrgIds ?? [])])].sort();
  return { orgId, role, adminOrgIds: reach };
}

/** Whether `actor` may act on documents belonging to `orgId`. */
export function reaches(
  actor: { orgId?: string | null; adminOrgIds?: string[] | null } | null | undefined,
  orgId: string
): boolean {
  if (!actor) return false;
  return actor.orgId === orgId || (actor.adminOrgIds ?? []).includes(orgId);
}
